import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "tabla-componentes" }
const _hoisted_2 = { class: "tabla-modern" }

import type Componente from '@/interfaces/Componente'
import type Indicador from '@/interfaces/Indicador'
import { computed, provide, reactive, ref, watch } from 'vue'
import { useComponentes } from '@/store/GestionComponentes'
import FilaComponente from './FilaComponente.vue'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useModelo } from '@/store/GestionModelo'
import type Instrumento from '@/interfaces/Instrumetos'


export default /*@__PURE__*/_defineComponent({
  __name: 'ModificarComponentes',
  props: {
    componentes: {},
    idComponenteRaiz: {},
    componenteEditandoId: {},
    esIndicadores: { type: Boolean },
    esIndicadorFn: {},
    puedeAcceder: { type: Boolean },
    indicador: {},
    instrumentosPorIndicadorFn: {},
    mostrarBotonCalificar: { type: Boolean },
    mostrarAccionesComponente: { type: Boolean, default: true },
    mostrarAccionesIndicador: { type: Boolean, default: true },
    ocultarFilaComponente: { type: Boolean, default: false },
    niveles: {},
    obtenerNumeracion: {},
    loading: { type: Boolean, default: false }
  },
  emits: ["modificar", "eliminar", "agregarHijo", "agregarInstrumento", "vincularFormulario", "modificarIndicador", "eliminarIndicador", "acceder", "calificarInstrumento", "calificarIndicador", "modificarInstrumento", "eliminarInstrumento", "reordenar"],
  setup(__props: any, { emit: __emit }) {

/**
 * Edición en lote de los componentes del árbol.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

const versionesStore = useGestionVersiones()
const modeloStore = useModelo()

const ultimoNivel = computed(() => {
  // Usar el prop niveles si está disponible, sino calcular desde el modelo
  if (props.niveles && props.niveles.length > 0) {
    return props.niveles.length
  }
  const modelo = modeloStore.modeloActual
  if (!modelo) return 1
  return (modelo.niveles?.length ?? 0) + 1
})

const props = __props

const emit = __emit

const store = useComponentes()

const dragState = reactive<{
  dragging: Componente | null
  dropTargetId: number | null
  dropSide: 'before' | 'after'
}>({ dragging: null, dropTargetId: null, dropSide: 'after' })
provide('dragState', dragState)

const sortedComponentes = computed(() =>
  [...props.componentes].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

const componenteSeleccionado = ref<Componente | null>(null)
const expandido = ref<Set<number>>(new Set())

// Shared selection state for calificar mode
const seleccionCalificarCompartida = ref<string | null>(null)
provide('seleccionCalificar', seleccionCalificarCompartida)

// Registro de conteo previo de hijos por componente (no reactivo)
const prevChildrenCount = new Map<number, number>()

watch(
  () => props.componentes,
  (lista) => {
    const next = new Set(expandido.value)

    lista.forEach(c => {
      const id = c.id_componente!
      const currentCount = c.children?.length ?? 0
      const previousCount = prevChildrenCount.get(id) // undefined = carga inicial

      // Expandir solo si: es la carga inicial y ya tiene hijos
      // O si acaba de recibir su primer hijo (0 → 1+)
      if (currentCount > 0 && (previousCount === undefined || previousCount === 0)) {
        next.add(id)
      }

      prevChildrenCount.set(id, currentCount)
    })

    expandido.value = next
  },
  { deep: true, immediate: true }
)

function toggleExpandido(id: number) {
  const next = new Set(expandido.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expandido.value = next
}

function onModificar(comp: Componente) {
  componenteSeleccionado.value = null
  emit('modificar', comp)
}

function onAgregarHijo(payload: { padre: Componente; nivel: number }) {
  componenteSeleccionado.value = null
  if (payload.padre?.id_componente) {
    const next = new Set(expandido.value)
    next.add(payload.padre.id_componente)
    expandido.value = next
  }
  emit('agregarHijo', payload)
}

function onAgregarInstrumento(indicador?: Indicador) {
  emit('agregarInstrumento', indicador)
}

function onModificarIndicador(indicador: Indicador) {
  emit('modificarIndicador', indicador)
}

function onEliminarIndicador(indicador: Indicador) {
  emit('eliminarIndicador', indicador)
}

function onAcceder(comp: Componente) {
  emit('acceder', comp)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("table", _hoisted_2, [
      _cache[8] || (_cache[8] = _createElementVNode("thead", null, [
        _createElementVNode("tr", null, [
          _createElementVNode("th", null, "Ordinal"),
          _createElementVNode("th", null, "Nombre"),
          _createElementVNode("th", null, "Acciones")
        ])
      ], -1)),
      _createElementVNode("tbody", null, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedComponentes.value, (comp, index) => {
          return (_openBlock(), _createBlock(FilaComponente, {
            key: comp.id_componente,
            comp: comp,
            nivel: 0,
            ultimoNivel: ultimoNivel.value,
            posicion: index + 1,
            totalHermanos: sortedComponentes.value.length,
            componenteSeleccionado: componenteSeleccionado.value,
            "onUpdate:componenteSeleccionado": _cache[0] || (_cache[0] = ($event: any) => ((componenteSeleccionado).value = $event)),
            expandido: expandido.value,
            toggleExpandido: toggleExpandido,
            esIndicadores: props.esIndicadores,
            esIndicadorFn: props.esIndicadorFn,
            puedeAcceder: props.puedeAcceder,
            instrumentosPorIndicadorFn: _ctx.instrumentosPorIndicadorFn,
            mostrarBotonCalificar: props.mostrarBotonCalificar,
            mostrarAccionesComponente: props.mostrarAccionesComponente,
            mostrarAccionesIndicador: props.mostrarAccionesIndicador,
            ocultarFilaComponente: props.ocultarFilaComponente,
            componenteEditandoId: props.componenteEditandoId,
            niveles: props.niveles,
            obtenerNumeracion: props.obtenerNumeracion,
            loading: props.loading,
            onModificar: onModificar,
            onEliminar: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('eliminar', $event))),
            onAgregarHijo: onAgregarHijo,
            onAgregarInstrumento: onAgregarInstrumento,
            onVincularFormulario: _cache[2] || (_cache[2] = ($event: any) => (emit('vincularFormulario', $event))),
            onModificarIndicador: onModificarIndicador,
            onEliminarIndicador: onEliminarIndicador,
            onAcceder: onAcceder,
            onCalificarInstrumento: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('calificarInstrumento', $event))),
            onCalificarIndicador: _cache[4] || (_cache[4] = ($event: any) => (_ctx.$emit('calificarIndicador', $event))),
            onModificarInstrumento: _cache[5] || (_cache[5] = ($event: any) => (_ctx.$emit('modificarInstrumento', $event))),
            onEliminarInstrumento: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$emit('eliminarInstrumento', $event))),
            onReordenar: _cache[7] || (_cache[7] = ($event: any) => (_ctx.$emit('reordenar', $event)))
          }, null, 8, ["comp", "ultimoNivel", "posicion", "totalHermanos", "componenteSeleccionado", "expandido", "esIndicadores", "esIndicadorFn", "puedeAcceder", "instrumentosPorIndicadorFn", "mostrarBotonCalificar", "mostrarAccionesComponente", "mostrarAccionesIndicador", "ocultarFilaComponente", "componenteEditandoId", "niveles", "obtenerNumeracion", "loading"]))
        }), 128))
      ])
    ])
  ]))
}
}

})